/**
 * What an alert row is ABOUT, read back out of it (twin slice 1, W5).
 *
 * The attention list joins a robot's live `issues[]` category, or a lift's
 * standing refusal code, to the alert the server raised for it. The server
 * stamps that identity into the row's `fingerprint`
 * (`robot_issue:<robot>:<category>`, `lift_refusal:<lift>:<code>`), which
 * `AlertOut` carries from api-server 0.6.37. An older server sends no
 * fingerprint, so the same identity is also read from the message -- whose
 * exact shape `tests/fixtures/contracts/alert-messages.json` pins on both
 * sides (the engine must emit it, this module must read it back).
 */
import type { AlertViewModel } from '../../api/adapters';

/**
 * The fields of an alert row the attention list reads.
 *
 * NOT a parallel record type (plan X12): W4 extends `AlertViewModel` with
 * `rule`, `sourceKind`, `sourceId`, `openedAtMs`, `fingerprint`,
 * `ackedByRoles` and `ackedByRole`, and this is the structural subset of
 * that view model, so the selector takes W4's rows unchanged. Every field
 * W4 adds is optional here, so today's `AlertViewModel` already fits.
 */
export type AttentionAlert = Pick<
  AlertViewModel,
  'id' | 'severity' | 'message' | 'ackedAtMs' | 'ackedBySub' | 'ackedByEmail'
> & {
  /** Today's view model carries the source id here. */
  source?: string;
  rule?: string;
  sourceKind?: string;
  sourceId?: string;
  fingerprint?: string;
  openedAtMs?: number;
  /** Bare role names, sorted (W7, migration 0035). Null before 0035. */
  ackedByRoles?: readonly string[] | null;
  /** The highest of `ackedByRoles` by the server's precedence (W7). */
  ackedByRole?: string | null;
};

export type AlertSubject =
  | { kind: 'robot_issue'; robot: string; category: string }
  | { kind: 'robot_error'; robot: string }
  | { kind: 'robot_unreachable'; robot: string }
  | { kind: 'task_attention'; task: string; robot: string | null; liftRule?: true }
  /** In progress past its category's limit (`task_long_running`).
   *  `minutes` is how long it had run when the row opened. */
  | { kind: 'task_long_running'; task: string; robot: string | null; minutes: number | null }
  | { kind: 'lift_refusal'; lift: string; code: string }
  | { kind: 'lift_fault'; lift: string }
  | { kind: 'lift_lanes_closed'; lift: string }
  | { kind: 'lift_no_reading'; lift: string }
  /** The site's lift write gate reads shut (A14): about the site, whose
   *  id is the row's source. */
  | { kind: 'lift_gate_disarmed'; site: string }
  | { kind: 'edge_gateway'; service: string }
  /** The whole site box silent, or only its health reports. About the
   *  site, named at the head of the message; the row's source is `box`. */
  | { kind: 'edge_box_offline'; site: string }
  | { kind: 'edge_agent_silent'; site: string }
  /** api-server 0.6.42 (2026-10-21). About the DOOR, the row's source:
   *  `door_open_failed`, the door did not open for whoever asked;
   *  `door_passage_stalled`, it opened for a robot that has not come
   *  through. The robot is never read out of the message: the console
   *  takes it from the door's live state (`select.ts`). */
  | { kind: 'door_open_failed'; door: string }
  | { kind: 'door_passage_stalled'; door: string }
  /** A robot on a job that has made no progress for a minute
   *  (api-server 0.6.42). */
  | { kind: 'robot_stalled'; robot: string };

/** The rule, from the row or else from its fingerprint's first segment. */
export function alertRule(alert: AttentionAlert): string | null {
  if (alert.rule) return alert.rule;
  const fp = alert.fingerprint;
  if (!fp) return null;
  const colon = fp.indexOf(':');
  return colon > 0 ? fp.slice(0, colon) : null;
}

function sourceIdOf(alert: AttentionAlert): string | null {
  return alert.sourceId || alert.source || null;
}

/** The text after `<rule>:<source>:` in the fingerprint, if it has one. */
function fingerprintTail(alert: AttentionAlert, rule: string, source: string): string | null {
  const head = `${rule}:${source}:`;
  const fp = alert.fingerprint;
  return fp && fp.startsWith(head) && fp.length > head.length ? fp.slice(head.length) : null;
}

/**
 * `"<robot> is reporting an issue: <category>."`, optionally followed by a
 * second sentence (the fire hold's). A category never contains ". ", so
 * the first one ends it.
 */
function categoryFromMessage(message: string, robot: string): string | null {
  // `NAV_STALLED` is worded as what to do, not as the category (api-server
  // with fleet adapter 0.7.33); its fixed opening says which it is.
  if (message.startsWith(`${robot} has a goal but isn't moving`)) return 'NAV_STALLED';
  const head = `${robot} is reporting an issue: `;
  if (!message.startsWith(head)) return null;
  const rest = message.slice(head.length);
  const stop = rest.indexOf('. ');
  const category = stop >= 0 ? rest.slice(0, stop) : rest.replace(/\.$/, '');
  return category.length > 0 ? category : null;
}

/** `"<lift> is refusing robot rides (<CODE>, permanent|repeated)..."`. */
function codeFromMessage(message: string, lift: string): string | null {
  const head = `${lift} is refusing robot rides (`;
  if (!message.startsWith(head)) return null;
  const m = /^([^,)]+), (?:permanent|repeated)\)/.exec(message.slice(head.length));
  return m ? m[1]! : null;
}

/** `"Task <task> on <robot> has needed attention ..."`; the engine writes
 *  "an unassigned robot" when there is none. */
function robotFromAttentionMessage(message: string, task: string): string | null {
  const head = `Task ${task} on `;
  if (!message.startsWith(head)) return null;
  const rest = message.slice(head.length);
  const end = rest.indexOf(' has needed attention');
  if (end <= 0) return null;
  const robot = rest.slice(0, end);
  return robot === 'an unassigned robot' ? null : robot;
}

/** `"Task <task> on <robot> has been running for over <N> min. ..."`; the
 *  engine writes "an unassigned robot" when there is none. */
function longRunningFromMessage(
  message: string,
  task: string,
): { robot: string | null; minutes: number | null } {
  const head = `Task ${task} on `;
  if (!message.startsWith(head)) return { robot: null, minutes: null };
  const rest = message.slice(head.length);
  const marker = ' has been running for over ';
  const end = rest.indexOf(marker);
  if (end <= 0) return { robot: null, minutes: null };
  const robot = rest.slice(0, end);
  const m = /^(\d+) min\b/.exec(rest.slice(end + marker.length));
  return {
    robot: robot === 'an unassigned robot' ? null : robot,
    minutes: m ? Number(m[1]) : null,
  };
}

/** `"<site> site box ..."`, the opening both of the box's rules share. */
function siteFromBoxMessage(message: string): string | null {
  const stop = message.indexOf(' site box ');
  return stop > 0 ? message.slice(0, stop) : null;
}

/** What the alert is about, or null for a rule the attention list does not
 *  read (or a row too malformed to say). */
export function alertSubject(alert: AttentionAlert): AlertSubject | null {
  const rule = alertRule(alert);
  const source = sourceIdOf(alert);
  if (!rule || !source) return null;
  switch (rule) {
    case 'robot_issue': {
      const category =
        fingerprintTail(alert, rule, source) ?? categoryFromMessage(alert.message, source);
      return category ? { kind: 'robot_issue', robot: source, category } : null;
    }
    case 'robot_error':
    case 'robot_unreachable':
      return { kind: rule, robot: source };
    case 'task_attention':
      return {
        kind: 'task_attention',
        task: source,
        robot: robotFromAttentionMessage(alert.message, source),
        ...(alert.message.includes(' Lift rule in force: ') ? { liftRule: true as const } : {}),
      };
    case 'task_long_running':
      return { kind: 'task_long_running', task: source, ...longRunningFromMessage(alert.message, source) };
    case 'lift_refusal': {
      const code = fingerprintTail(alert, rule, source) ?? codeFromMessage(alert.message, source);
      return code ? { kind: 'lift_refusal', lift: source, code } : null;
    }
    case 'lift_fault':
    case 'lift_lanes_closed':
    case 'lift_no_reading':
      return { kind: rule, lift: source };
    case 'lift_gate_disarmed':
      return { kind: 'lift_gate_disarmed', site: source };
    case 'edge_gateway':
      return { kind: 'edge_gateway', service: source };
    case 'edge_box_offline':
    case 'edge_agent_silent': {
      const site = fingerprintTail(alert, rule, source) ?? siteFromBoxMessage(alert.message);
      return site ? { kind: rule, site } : null;
    }
    // Identity is the rule and its source alone, as the engine
    // fingerprints them (`<rule>:<door>`, `<rule>:<robot>`): no message
    // shape is read.
    case 'door_open_failed':
    case 'door_passage_stalled':
      return { kind: rule, door: source };
    case 'robot_stalled':
      return { kind: 'robot_stalled', robot: source };
    default:
      return null;
  }
}

/**
 * The row's fingerprint: the server's own when it sent one, else rebuilt
 * from the subject by the engine's formula. Null when neither is possible.
 */
export function alertFingerprint(alert: AttentionAlert): string | null {
  if (alert.fingerprint) return alert.fingerprint;
  const rule = alertRule(alert);
  const source = sourceIdOf(alert);
  if (!rule || !source) return null;
  const subject = alertSubject(alert);
  if (subject?.kind === 'robot_issue') return `${rule}:${source}:${subject.category}`;
  if (subject?.kind === 'lift_refusal') return `${rule}:${source}:${subject.code}`;
  if (subject?.kind === 'edge_box_offline' || subject?.kind === 'edge_agent_silent') {
    return `${rule}:${source}:${subject.site}`;
  }
  // A robot_issue or lift_refusal row whose tail could not be read has no
  // fingerprint we can vouch for: `robot_issue:<robot>` alone would match
  // nothing the server ever wrote.
  if (rule === 'robot_issue' || rule === 'lift_refusal') return null;
  return `${rule}:${source}`;
}

/** The link codes `alerts_engine._LIFT_LINK_CODES` names. */
const LIFT_LINK_CODES: ReadonlySet<string> = new Set(['LINK_DOWN', 'TELEMETRY_STALE']);

/**
 * Whether this row only repeats a silent site box, and what of the box's it
 * lost sight of: `alerts_engine._contact_lost_family`, the same answer from
 * the same row (`contactLost` in `alert-messages.json` holds the two equal).
 * '' is the box itself; null is a real report, which no silence explains.
 */
export function contactLostKind(alert: AttentionAlert): 'lift' | 'robot' | 'door' | '' | null {
  const subject = alertSubject(alert);
  switch (alertRule(alert)) {
    case 'robot_issue':
      return subject?.kind === 'robot_issue' && subject.category === 'lost' ? 'robot' : null;
    case 'lift_fault':
      return alert.message.startsWith("Nexsoss can't see ") ? 'lift' : null;
    case 'lift_no_reading':
      return 'lift';
    case 'lift_refusal':
      return subject?.kind === 'lift_refusal' && LIFT_LINK_CODES.has(subject.code) ? 'lift' : null;
    case 'edge_gateway':
      return alert.message.includes(' service is unreachable') ? '' : null;
    case 'door_unknown':
    case 'door_adapter_silent':
      return 'door';
    default:
      return null;
  }
}
